// src/app/auth/forgot-password/forgot-password.component.ts
import { Component, inject, signal, computed, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {AsyncPipe, NgOptimizedImage} from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialogRef } from '@angular/material/dialog';
import { CodeInputModule } from 'angular-code-input';
import { interval, Subscription } from 'rxjs';

import { OtpService } from '../services/otp.service';
import { OtpQuery } from '../state/otp.query';
import { OtpPurpose } from '../../models/otp.models';
import { DeviceService } from '../../ngm-dev-blocks/utils/services';

enum ForgotPasswordStep {
  EMAIL = 1,
  VERIFY_OTP = 2,
  RESET_PASSWORD = 3
}

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgOptimizedImage,
    MatProgressSpinner,
    CodeInputModule,
    AsyncPipe
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private snackBar = inject(MatSnackBar);
  private dialogRef = inject(MatDialogRef<ForgotPasswordComponent>);
  private otpService = inject(OtpService);
  private otpQuery = inject(OtpQuery);
  private deviceService = inject(DeviceService);

  // Device observables
  isHandset$ = this.deviceService.isHandset$;
  isTablet$ = this.deviceService.isTablet$;
  isLessThanMD$ = this.deviceService.isLessThanMD$;

  // State signals
  currentStep = signal<ForgotPasswordStep>(ForgotPasswordStep.EMAIL);
  email = signal<string>('');
  otpCode = signal<string>('');

  // OTP state from Akita
  loading = this.otpQuery.loading;
  error = this.otpQuery.error;

  // Timer for OTP expiry
  timeRemaining = signal<number>(600);
  canResend = signal<boolean>(false);
  private timerSubscription?: Subscription;

  // Computed
  minutes = computed(() => Math.floor(this.timeRemaining() / 60));
  seconds = computed(() => this.timeRemaining() % 60);

  Step = ForgotPasswordStep;

  // Forms
  emailForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  resetPasswordForm: FormGroup = this.fb.group({
    newPassword: ['', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(72),
      Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*\W).*$/)
    ]],
    confirmPassword: ['', [Validators.required]]
  }, { validators: this.passwordMatchValidator });

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  requestOtp() {
    if (this.emailForm.valid) {
      const email = this.emailForm.get('email')?.value;
      this.email.set(email);

      this.otpService.generateOtp(email, OtpPurpose.PASSWORD_RESET).subscribe({
        next: () => {
          this.showToast('Code OTP envoyé à votre email', 'success');
          this.currentStep.set(ForgotPasswordStep.VERIFY_OTP);
          this.startTimer();
        },
        error: (error) => {
          this.showToast(error.error?.message || 'Erreur lors de l\'envoi du code OTP', 'error');
        }
      });
    }
  }

  onCodeCompleted(code: string) {
    this.otpCode.set(code);


    this.showToast('Code saisi avec succès', 'success');
    this.stopTimer();
    this.currentStep.set(ForgotPasswordStep.RESET_PASSWORD);
  }

  onCodeChanged(code: string) {
    this.otpCode.set(code);
  }

  resendOtp() {
    if (!this.canResend()) return;

    this.otpService.resendOtp(this.email(), OtpPurpose.PASSWORD_RESET).subscribe({
      next: () => {
        this.showToast('Nouveau code OTP envoyé', 'success');
        this.timeRemaining.set(600);
        this.canResend.set(false);
        this.startTimer();
      },
      error: (error) => {
        this.showToast(error.error?.message || 'Erreur lors du renvoi du code', 'error');
      }
    });
  }

  resetPassword() {
    if (this.resetPasswordForm.valid) {
      const request = {
        email: this.email(),
        otpCode: this.otpCode(),
        newPassword: this.resetPasswordForm.get('newPassword')?.value,
        confirmPassword: this.resetPasswordForm.get('confirmPassword')?.value
      };

      // OTP is verified HERE in the backend
      this.otpService.resetPassword(request).subscribe({
        next: () => {
          this.showToast('Mot de passe réinitialisé avec succès', 'success');
          this.dialogRef.close();
        },
        error: (error) => {
          this.showToast(error.error?.message || 'Erreur lors de la réinitialisation', 'error');
        }
      });
    }
  }

  private startTimer() {
    this.timerSubscription = interval(1000).subscribe(() => {
      const current = this.timeRemaining();
      if (current > 0) {
        this.timeRemaining.set(current - 1);
      } else {
        this.stopTimer();
        this.canResend.set(true);
      }
    });
  }

  private stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  goBack() {
    if (this.currentStep() === ForgotPasswordStep.EMAIL) {
      this.dialogRef.close();
    } else {
      this.currentStep.update(step => step - 1);
      if (this.currentStep() === ForgotPasswordStep.EMAIL) {
        this.stopTimer();
      }
    }
  }

  private showToast(message: string, type: 'success' | 'error') {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [`toast-${type}`]
    });
  }

  ngOnDestroy() {
    this.stopTimer();
    this.otpService.clearState();
  }
}
